import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import '../housestyle.css';
import './Home.css';
import api from '../API/Api';
import { FadeIn, StaggerContainer, StaggerItem } from '../components/Animations';
import { McIcon } from '../components/CraftingGrid';
import DiscordMarkdown from '../components/DiscordMarkdown';
import { getMobWikiUrl } from '../data/wiki_mobs_assets';

const RARITY_COLORS = {
  common: '#9e9e9e',
  uncommon: '#52c078',
  rare: '#5a9ee0',
  epic: '#a070e0',
  legendary: '#f0ab20',
  unique: '#d94f4f',
  boss: '#e84c3d',
};

const RARITY_ORDER = ['legendary', 'unique', 'boss', 'epic', 'rare', 'uncommon', 'common'];

// Maps mob tier (number or string) → display label, null = hidden
const MOB_TIER_LABEL = {
  0: null, common: null,          // minions — hidden
  1: 'Tier 1', uncommon: 'Tier 1',
  2: 'Tier 2', rare:     'Tier 2',
  3: 'Tier 3', epic:     'Tier 3',
  4: 'Tier 4', legendary:'Tier 4',
  boss: 'Boss', unique: 'Unique',
};

const HIDDEN_ITEM_RARITIES = new Set(['common', 'uncommon']);

function normalise(items, mobs) {
  const ni = items
    .filter(i => Number(i.enabled) !== 0 && !HIDDEN_ITEM_RARITIES.has(i.rarity))
    .map(i => ({ ...i, _kind: 'item', _rarity: i.rarity || 'common' }));

  const nm = mobs
    .filter(m => {
      if (Number(m.enabled) === 0) return false;
      const tier = m.tier ?? m.rarity;
      return MOB_TIER_LABEL[tier] !== null && MOB_TIER_LABEL[tier] !== undefined;
    })
    .map(m => ({ ...m, _kind: 'mob', _rarity: m.tier ?? m.rarity ?? 'common' }));

  return [...ni, ...nm].sort((a, b) => {
    const ai = RARITY_ORDER.indexOf(String(a._rarity));
    const bi = RARITY_ORDER.indexOf(String(b._rarity));
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });
}

const ICON_SIZE = 56;

function EntryIcon({ entry }) {
  if (entry._kind === 'item') {
    const mat = (entry.material || entry.type || 'barrier').toLowerCase();
    return <McIcon materialKey={mat} size={ICON_SIZE} />;
  }
  const url = getMobWikiUrl(entry.type, entry.name);
  if (url) {
    return <img src={url} alt={entry.name} className="home-sp-mob-icon" />;
  }
  return <McIcon materialKey="nether_star" size={ICON_SIZE} />;
}

function rarityLabel(entry) {
  if (entry._kind === 'mob') {
    return MOB_TIER_LABEL[entry._rarity] ?? `Tier ${entry._rarity}`;
  }
  return entry._rarity;
}

function SpotlightCard({ entry }) {
  const rarity = entry._rarity;
  const color = RARITY_COLORS[rarity] || '#9e9e9e';
  const slug = entry.slug || entry.id;
  const href = entry._kind === 'item' ? `/wiki/item/${slug}` : `/wiki/mob/${slug}`;
  return (
    <div className="home-sp-card" style={{ '--rc': color }}>
      <div className="home-sp-bar" />
      <div className="home-sp-icon">
        <div className="home-sp-icon-glow" style={{ '--rc': color }}>
          <EntryIcon entry={entry} />
        </div>
      </div>
      <span className="home-sp-rarity">{rarityLabel(entry)}</span>
      <h4 className="home-sp-name">{entry.name}</h4>
      <p className="home-sp-desc">{entry.description}</p>
      <div className="home-sp-foot">
        <span className="home-sp-abilities">
          {entry.abilities?.length ?? 0} {(entry.abilities?.length ?? 0) === 1 ? 'ability' : 'abilities'}
        </span>
        <Link to={href} className="home-sp-view">View in Wiki →</Link>
      </div>
    </div>
  );
}

const VISIBLE = 3;
const MAX_DOTS = 7;

function DotsNav({ total, current, onGo }) {
  if (total <= 1) return null;

  let start = 0;
  let end = total;
  let showLeading = false;
  let showTrailing = false;

  if (total > MAX_DOTS) {
    const half = Math.floor(MAX_DOTS / 2);
    start = Math.max(0, current - half);
    end = start + MAX_DOTS;
    if (end > total) { end = total; start = Math.max(0, end - MAX_DOTS); }
    showLeading = start > 0;
    showTrailing = end < total;
  }

  return (
    <div className="home-sp-dots">
      {showLeading && <span className="home-sp-dots-ellipsis">…</span>}
      {Array.from({ length: end - start }, (_, i) => {
        const n = start + i;
        return (
          <button
            key={n}
            className={`home-sp-dot${n === current ? ' active' : ''}`}
            onClick={() => onGo(n)}
          />
        );
      })}
      {showTrailing && <span className="home-sp-dots-ellipsis">…</span>}
    </div>
  );
}

function WikiSpotlight({ items, mobs }) {
  const entries = normalise(items, mobs);
  const max = Math.max(0, entries.length - VISIBLE);

  const [idx, setIdx] = useState(0);
  const [anim, setAnim] = useState(true);
  const timerRef = useRef(null);

  function startTimer() {
    clearInterval(timerRef.current);
    if (entries.length <= VISIBLE) return;
    timerRef.current = setInterval(() => {
      setIdx(i => {
        if (i >= max) {
          setAnim(false);
          requestAnimationFrame(() => requestAnimationFrame(() => setAnim(true)));
          return 0;
        }
        setAnim(true);
        return i + 1;
      });
    }, 3800);
  }

  useEffect(() => {
    startTimer();
    return () => clearInterval(timerRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entries.length]);

  function goTo(n) {
    setAnim(true);
    setIdx(Math.max(0, Math.min(n, max)));
    startTimer();
  }

  if (entries.length === 0) return null;

  return (
    <FadeIn duration={0.7}>
      <div className="home-spotlight">
        <div className="home-sp-header">
          <div className="section-label" style={{ margin: 0 }}>
            <span className="sl-text">Featured from the Wiki</span>
          </div>
          <Link to="/wiki" className="home-sp-browse">Browse Wiki →</Link>
        </div>

        <div className="home-sp-viewport">
          <div
            className="home-sp-track"
            style={{
              transform: `translateX(calc(-${idx} * (100% / ${VISIBLE})))`,
              transition: anim ? 'transform 0.45s cubic-bezier(0.4, 0, 0.2, 1)' : 'none',
            }}
          >
            {entries.map(e => (
              <SpotlightCard key={`${e._kind}-${e.slug || e.id}`} entry={e} />
            ))}
          </div>
        </div>

        {entries.length > VISIBLE && (
          <div className="home-sp-controls">
            <button className="home-sp-arrow" onClick={() => goTo(idx - 1)}>‹</button>
            <DotsNav total={max + 1} current={idx} onGo={goTo} />
            <button className="home-sp-arrow" onClick={() => goTo(idx + 1)}>›</button>
          </div>
        )}
      </div>
    </FadeIn>
  );
}

function IPBar() {
  const [toastVisible, setToastVisible] = useState(false);
  const timerRef = useRef(null);

  function copyIP() {
    navigator.clipboard.writeText('play.simplemcnetwork.net').catch(() => { });
    setToastVisible(true);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setToastVisible(false), 1800);
  }

  return (
    <>
      <div className="home-ip-bar">
        <div className="home-ip-item">
          <span className="home-ip-label">IP</span>
          <span className="home-ip-value">play.simplemcnetwork.net</span>
          <button className="home-ip-copy" onClick={copyIP}>Copy</button>
        </div>
        <div className="home-ip-divider" />
        <div className="home-ip-item">
          <span className="home-ip-label">Version</span>
          <span className="home-ip-value">1.21+</span>
        </div>
        <div className="home-ip-divider" />
        <div className="home-ip-item">
          <span className="home-ip-label">Status</span>
          <span className="home-ip-value home-ip-value--development">● in development</span>
        </div>
        <div className="home-ip-divider" />
        <div className="home-ip-item">
          <span className="home-ip-label">Mode</span>
          <span className="home-ip-value" style={{ color: 'var(--text-primary)', fontWeight: 600 }}>Java</span>
        </div>
        <div className="home-ip-divider" />
        <div className="home-ip-item">
          <span className="home-ip-label">Cracked?</span>
          <span className="home-ip-value" style={{ color: 'var(--text-primary)', fontWeight: 600 }}>Yes</span>
        </div>
      </div>
      <div className={`home-copied-toast${toastVisible ? ' show' : ''}`}>IP Copied!</div>
    </>
  );
}

const ABOUT_DECO = [
  { mat: 'diamond_sword',        color: '#e84c3d', rotate: -18, top:  8,  left:  20 },
  { mat: 'netherite_chestplate', color: '#a070e0', rotate:  10, top:  0,  left:  90 },
  { mat: 'golden_apple',         color: '#f0ab20', rotate:  -6, top: 72,  left:   0 },
  { mat: 'ender_pearl',          color: '#5a9ee0', rotate:  14, top: 68,  left: 100 },
  { mat: 'nether_star',          color: '#ffffff', rotate:   3, top: 38,  left:  58 },
];

function AboutSection() {
  return (
    <FadeIn duration={0.7}>
      <div className="home-about">
        <div className="home-about-text">
          <div className="home-eyebrow"><span className="home-eyebrow-badge">Season 1</span></div>
          <h2>Vanilla is dead.<br /><span>This is what's next.</span></h2>
          <p>
            Custom mobs that actually fight back. Weapons you forge from drops no one else has found yet.
            Bosses that will end your run without warning. SimpleMC isn't a server you log into —
            it's a game you haven't played before.
          </p>
          <div className="home-about-btns">
            <button className="btn btn-primary">Join Now</button>
            <button className="btn btn-secondary"
              onClick={() => window.open('https://discord.com/invite/simplemc', '_blank')}>
              Discord
            </button>
          </div>
        </div>

        <div className="home-about-deco" aria-hidden="true">
          {ABOUT_DECO.map((d, i) => (
            <div
              key={i}
              className="home-about-deco-item"
              style={{ '--c': d.color, '--r': `${d.rotate}deg`, top: `${d.top}%`, left: `${d.left}%` }}
            >
              <McIcon materialKey={d.mat} size={50} />
            </div>
          ))}
        </div>
      </div>
    </FadeIn>
  );
}

function FeatureCard({ material, title, desc, barClass }) {
  return (
    <div className="home-feat-card">
      <div className={`home-feat-bar ${barClass}`} />
      <McIcon materialKey={material} size={32} />
      <h3>{title}</h3>
      <p>{desc}</p>
    </div>
  );
}

function Step({ num, title, children }) {
  return (
    <div className="home-step">
      <div className="home-step-num">{num}</div>
      <h4>{title}</h4>
      <p>{children}</p>
    </div>
  );
}

function NewsCard({ item }) {
  const isOffer = item.type === 'offer';
  return (
    <div className={`home-news-card ${isOffer ? 'home-news-card--offer' : ''} ${item.image_url ? 'home-news-card--has-img' : ''}`}>
      <div className="home-news-content">
        <div className="home-news-header">
          <span className={`home-news-badge ${item.type}`}>
            {isOffer ? 'Limited Offer' : item.type}
          </span>
          <span className="home-news-date">
            {new Date(item.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
          </span>
        </div>
        <h3>{item.title}</h3>
        <DiscordMarkdown text={item.content} className="home-news-body" />
        <div className="home-news-footer">
          <span className="home-news-author">By {item.author || 'Staff'}</span>
        </div>
      </div>
      {item.image_url && (
        <div className="home-news-img">
          <img src={item.image_url} alt={item.title} />
        </div>
      )}
    </div>
  );
}

const features = [
  { material: 'DIAMOND_SWORD', title: 'Custom Weapons', desc: 'New weapons, armour, tools and other items — crafted in-game with drops from various sources.', barClass: 'home-feat-bar--red' },
  { material: 'REDSTONE', title: 'Custom Mobs', desc: "Unique bosses and mob variants with tiered drops. Explore the world and find what's lurking.", barClass: 'home-feat-bar--blue' },
  { material: 'CHEST_MINECART', title: 'Supply Drops', desc: 'Random supply crates scattered across the map. Loot them before someone else does.', barClass: 'home-feat-bar--green' },
  { material: 'FIREWORK_ROCKET', title: 'Events', desc: 'Regular seasonal events and competitions with exclusive rewards and community engagement.', barClass: 'home-feat-bar--gold' },
  { material: 'GOLD_INGOT', title: 'Fun Community', desc: 'Active player base with regular polls, staff interaction, and a welcoming environment for all.', barClass: 'home-feat-bar--purple' },
  { material: 'OAK_PLANKS', title: 'Good Anticheat', desc: 'Advanced cheat detection keeps the playing field fair for everyone.', barClass: 'home-feat-bar--blue' },
  { material: 'OMINOUS_TRIAL_KEY', title: 'Cracked Supported', desc: 'Play with or without a premium Minecraft account. Everyone welcome.', barClass: 'home-feat-bar--green' },
  { material: 'MAP', title: 'New POIs', desc: 'Explore unique locations with interesting stories and challenges.', barClass: 'home-feat-bar--gold' },
];

function useCountdown(targetDate) {
  const calc = () => {
    if (!targetDate) return { days: 0, hours: 0, minutes: 0, seconds: 0, ended: false };
    const diff = new Date(targetDate) - new Date();
    if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, ended: true };
    return {
      days: Math.floor(diff / 86400000),
      hours: Math.floor((diff % 86400000) / 3600000),
      minutes: Math.floor((diff % 3600000) / 60000),
      seconds: Math.floor((diff % 60000) / 1000),
      ended: false,
    };
  };
  const [time, setTime] = useState(calc);
  useEffect(() => {
    if (!targetDate) return;
    const t = setInterval(() => setTime(calc()), 1000);
    return () => clearInterval(t);
  }, [targetDate]);
  return time;
}

function LaunchBanner({ season }) {
  const launchDate = season?.start_date ?? null;
  const countdown = useCountdown(launchDate);
  const isLive = season?.is_live === '1' || season?.is_live === 1;

  if (!season) return null;

  return (
    <div className="home-launch-banner">
      <div className="home-launch-inner container">
        {isLive || countdown.ended ? (
          <>
            <div className="home-launch-label">Season 1 is</div>
            <div className="home-launch-live">LIVE</div>
            <div className="home-launch-ip">play.simplemcnetwork.net</div>
          </>
        ) : (
          <>
            <div className="home-launch-label">Season 1 launches in</div>
            <div className="home-launch-countdown">
              {[['Days', countdown.days], ['Hours', countdown.hours], ['Min', countdown.minutes], ['Sec', countdown.seconds]].map(([label, val]) => (
                <div key={label} className="home-launch-cd-block">
                  <span className="home-launch-cd-val">{String(val).padStart(2, '0')}</span>
                  <span className="home-launch-cd-label">{label}</span>
                </div>
              ))}
            </div>
            {launchDate && (
              <div className="home-launch-date">
                {new Date(launchDate).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default function HomePage() {
  const [visits, setVisits] = useState(null);
  const [news, setNews] = useState([]);
  const [wikiItems, setWikiItems] = useState([]);
  const [wikiMobs, setWikiMobs] = useState([]);
  const [season, setSeason] = useState(null);

  useEffect(() => {
    api('trackvisit').then(res => {
      if (res.isSuccess) setVisits(res.data?.visits ?? null);
    });
    api('getnews').then(res => {
      if (res.isSuccess) setNews(res.data || []);
    });
    api('getwikiitems').then(res => {
      if (res.isSuccess) setWikiItems(res.data || []);
    });
    api('getwikimobs').then(res => {
      if (res.isSuccess) setWikiMobs(res.data || []);
    });
    api('getseasoninfo').then(res => {
      if (res.isSuccess) setSeason(res.data);
    });
  }, []);

  return (
    <div style={{ paddingBottom: '4rem' }}>

      <div className="home-hero">
        <video autoPlay muted loop playsInline src="https://pub-332780ae20b540038a9ea772f1bf747c.r2.dev/Trailer.mp4" />
        <div className="home-hero-overlay" />
        <div className="home-hero-logo">
          <FadeIn y={30} duration={0.8}>
            <div className="home-hero-logo-text">Simple<em>MC</em></div>
            <div className="home-hero-logo-sub">Season 1 | Survival SMP</div>
            <div className="home-hero-logo-text-medium">IN DEVELOPMENT</div>
          </FadeIn>
        </div>
        <div className="home-hero-video-label">Website Visits: {visits}</div>
      </div>

      <IPBar />

      <LaunchBanner season={season} />

      <div className="container" style={{ paddingTop: '3.5rem' }}>

        <WikiSpotlight items={wikiItems} mobs={wikiMobs} />

        <AboutSection />

        <FadeIn duration={0.8}>
          <div className="section-label" style={{ marginTop: '3rem', marginBottom: '1.5rem' }}>
            <span className="sl-text">Latest News & Offers</span>
          </div>
          {news.length > 0 ? (
            <div className="home-news-grid">
              {news.slice(0, 3).map(item => <NewsCard key={item.id} item={item} />)}
            </div>
          ) : (
            <div className="home-news-empty">
              <p>No news or offers at the moment. Check back soon!</p>
            </div>
          )}
        </FadeIn>

        <div className="section-label" style={{ marginBottom: '1.25rem' }}>
          <span className="sl-text">New Experience</span>
        </div>

        <StaggerContainer className="home-features-grid">
          {features.map((f, i) => (
            <StaggerItem key={i}>
              <FeatureCard material={f.material} title={f.title} desc={f.desc} barClass={f.barClass} />
            </StaggerItem>
          ))}
        </StaggerContainer>

        <div className="section-label" style={{ marginBottom: '1.25rem' }}>
          <span className="sl-text">How to join</span>
        </div>

        <div className="home-join-card">
          <StaggerContainer className="home-steps">
            <StaggerItem><Step num="1" title="Open Minecraft">Launch Java Edition 1.21+ or higher on your PC.</Step></StaggerItem>
            <StaggerItem><Step num="2" title="Add Server">Go to Multiplayer → Add Server and give it a name.</Step></StaggerItem>
            <StaggerItem>
              <Step num="3" title="Enter IP">
                Server address:{' '}
                <span className="home-step-ip">play.simplemcnetwork.net</span>
              </Step>
            </StaggerItem>
            <StaggerItem><Step num="4" title="Join & Play">Hit connect and spawn in. Prepare for the unexpected.</Step></StaggerItem>
          </StaggerContainer>
        </div>

      </div>
    </div>
  );
}
